import React, { useEffect, useState } from "react";
import { GetFlavorsType } from "../../../models/_publicCall";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import { Tooltip } from "react-tooltip";

const FlavorPrices = ({
  flavors,
  jobId,
  disabled = false,
  jobStatus,
  installationJobDB
}: {
  flavors: GetFlavorsType[];
  jobId: string;
  disabled?: boolean;
  jobStatus: string;
  installationJobDB: any;
}) => {
  const [enabledFlavors, setEnabledFlavors] = useState<{ [key: string]: boolean }>({});
  const [flavorPrices, setFlavorPrices] = useState<any[]>([]);

  // 🔹 Save to localStorage (installationJob)
  const updateStore = (newPrices: any[]) => {
    setFlavorPrices(newPrices);

    const storedData = JSON.parse(localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}");

    const updatedData = {
      ...storedData,
      locationInformation: {
        ...storedData.locationInformation,
        flavorPrices: newPrices, // ✅ add/update flavorPrices here
      },
    };

    localStorage.setItem(INSTALLATION_JOB_STORAGE_KEY, JSON.stringify(updatedData));

  };

  // 🔹 Price input change
  const handleFlavorPriceChange = (flavorKey: string, value: string) => {
    const newPrices = flavorPrices.map((item: any) =>
      item.flavor === flavorKey
        ? { ...item, price: value, is_active: enabledFlavors[flavorKey] }
        : item
    );
    updateStore(newPrices);
  };

  // 🔹 Checkbox change
  const handleCheckboxChange = (flavorKey: string) => {
    const updated = {
      ...enabledFlavors,
      [flavorKey]: !enabledFlavors[flavorKey],
    };
    setEnabledFlavors(updated);

    const newPrices = flavorPrices.map((item: any) =>
      item.flavor === flavorKey
        ? { ...item, is_active: !enabledFlavors[flavorKey], price: item.price }
        : item
    );
    updateStore(newPrices);
  };

  // 🔹 Default pricing
  const setDefaultPricing = () => {
    const defaultPrices = flavors.map((flavor: any) => {
      const prev = flavorPrices.find((item: any) => item.flavor === flavor.flavor);
      const price = flavor.defaultPrice ?? 0;
      return {
        flavor: flavor.flavor,
        price: String(price),
        is_active: prev?.is_active ?? true,
      };
    });

    setEnabledFlavors(
      Object.fromEntries(defaultPrices.map((item: any) => [item.flavor, item.is_active]))
    );
    updateStore(defaultPrices);
  };

  // 🔹 Initialize from localStorage/flavors
  useEffect(() => {
    if (!flavors || flavors.length === 0) return;

    const storedData = JSON.parse(localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}");
    const storedPrices = storedData?.locationInformation?.flavorPrices;

    if (storedPrices && storedPrices.length > 0) {
      // ✅ Use localStorage values first
      setFlavorPrices(storedPrices);
      setEnabledFlavors(
        Object.fromEntries(storedPrices.map((item: any) => [item.flavor, !!item.is_active]))
      );
      return; // stop here, don't overwrite with DB
    }

    // Fallback: pre-populate from installationJobDB if localStorage empty
    if (jobStatus !== "Completed" && installationJobDB?.flavorPrice?.length > 0) {
      const dbPrices = installationJobDB.flavorPrice.map((fp: any) => ({
        flavor: fp.flavor,
        price: String(fp.price ?? 0),
        is_active: fp.is_active ?? true,
      }));

      setFlavorPrices(dbPrices);
      setEnabledFlavors(
        Object.fromEntries(dbPrices.map((item: any) => [item.flavor, item.is_active]))
      );

      // Save initial DB data to localStorage
      const updatedData = {
        ...storedData,
        locationInformation: {
          ...storedData.locationInformation,
          flavorPrices: dbPrices,
        },
      };
      localStorage.setItem(INSTALLATION_JOB_STORAGE_KEY, JSON.stringify(updatedData));
      return;
    }

    // Default pricing if both localStorage and DB are empty
    const defaultPrices = flavors.map((flavor: any) => ({
      flavor: flavor.flavor,
      price: String(flavor.defaultPrice ?? 0),
      is_active: true,
    }));
    setFlavorPrices(defaultPrices);
    setEnabledFlavors(
      Object.fromEntries(defaultPrices.map((item: any) => [item.flavor, item.is_active]))
    );
    updateStore(defaultPrices);

    // eslint-disable-next-line
  }, [flavors, jobId]);


  return (
    <div className="accordion mt-4" id="flavorPricesAccordion">
      <div className="accordion-item">
        <h2 className="accordion-header" id="flavorPricesHeading">
          <button
            className="accordion-button collapsed py-5 fs-4 fw-bold bg-light"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#flavorPricesCollapse"
            aria-expanded="false"
            aria-controls="flavorPricesCollapse"
          >
            Flavor Price
          </button>
        </h2>
        <div
          id="flavorPricesCollapse"
          className="accordion-collapse collapse"
          aria-labelledby="flavorPricesHeading"
          data-bs-parent="#flavorPricesAccordion"
        >
          <div className="accordion-body">
            <div className="row">
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary float-end flavor-price-default-btn"
                  onClick={setDefaultPricing}
                  disabled={disabled}
                >
                  Default Pricing
                </button>
                <Tooltip anchorSelect=".flavor-price-default-btn" data-placement="top">
                  Reset all flavors to their default prices
                </Tooltip>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="fw-bold mb-0">Flavor</th>
                      <th className="fw-bold mb-0">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flavorPrices.map((flavor: any) => {
                      const flavorKey = flavor.flavor;
                      const currentFlavor = flavors?.find(
                        (item: any) => item.flavor === flavorKey
                      );
                      const isEnabled = enabledFlavors[flavorKey] ?? true;
                      return (
                        <tr key={flavorKey}>
                          <td className="align-middle">
                            <input
                              className="form-check-input mb-2 me-2"
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => handleCheckboxChange(flavorKey)}
                              disabled={disabled}
                            />
                            {currentFlavor?.name || flavorKey}
                          </td>
                          <td className="align-middle">
                            <input
                              type="number"
                              className="form-control mb-1"
                              value={flavor?.price ?? ""}
                              min={0}
                              onChange={(e) =>
                                handleFlavorPriceChange(flavorKey, e.target.value)
                              }
                              disabled={!isEnabled || disabled}
                              step="any"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlavorPrices;
